/*
 * Yoni benefit cards — carousel controls + the per-card "how it works" panel.
 *
 * The track is a real scroll container, so swipe, trackpad and keyboard work
 * natively; this only adds arrows, position dots and the expand/collapse.
 * Scrolling is stepped by the measured card width (no magic numbers) and falls
 * back to an instant jump when the visitor prefers reduced motion.
 * The row is endless (assets/yoni-loop.js, loaded first) unless data-loop="false":
 * copies of the cards sit before and after the real ones, so card lists below
 * include them and a card's real position is its index modulo the card count.
 * Cards are .yoni-bcards__card unless the row names its own with data-bcards-card="<selector>"
 * (the journal's full-width row uses it for its stories: same dots, arrows, loop and glide).
 * Re-initialises on shopify:section:load so the theme editor stays usable.
 */
(function () {
  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function reducedMotion() {
    return (
      document.body.classList.contains('yoni-motion-off') ||
      (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)
    );
  }

  function init(root) {
    if (root.dataset.yoniBcardsInit === '1') return;
    root.dataset.yoniBcardsInit = '1';

    var track = root.querySelector('[data-bcards-track]');
    if (!track) return;
    var CARD = root.getAttribute('data-bcards-card') || '.yoni-bcards__card';
    var cards = Array.prototype.slice.call(track.querySelectorAll(':scope > ' + CARD));
    if (!cards.length) return;
    var n = cards.length;
    var loop = root.getAttribute('data-loop') !== 'false' && window.yoniLoop ? window.yoniLoop(track) : null;
    function all() { return Array.prototype.slice.call(track.querySelectorAll(':scope > ' + CARD)); }

    var dotsWrap = root.querySelector('[data-bcards-dots]');
    var prev = root.querySelector('[data-bcards-prev]');
    var next = root.querySelector('[data-bcards-next]');

    // ---- expand / collapse --------------------------------------------------
    function setOpen(card, open) {
      card.classList.toggle('is-open', open);
      var btn = card.querySelector('[data-bcards-more]');
      var panel = card.querySelector('.yoni-bcards__detail');
      if (btn) btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (panel) panel.setAttribute('aria-hidden', open ? 'false' : 'true');
    }

    // Delegated, so the loop's copies work too. A card opens on every copy of it, so the
    // loop's jump between copies never shows a panel snapping shut.
    track.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-bcards-more]');
      if (!btn) return;
      var list = all();
      var k = list.indexOf(btn.closest('.yoni-bcards__card')) % n;
      var open = !btn.closest('.yoni-bcards__card').classList.contains('is-open');
      // One panel open at a time — two open panels read as a broken row.
      list.forEach(function (card, i) { setOpen(card, open && i % n === k); });
    });

    root.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      var real = cards.filter(function (c) { return c.classList.contains('is-open'); })[0];
      if (!real) return;
      all().forEach(function (card) { setOpen(card, false); });
      var btn = real.querySelector('[data-bcards-more]');
      if (btn) btn.focus();
    });

    // ---- stepping -----------------------------------------------------------
    // Positions are measured from the live geometry rather than computed as
    // index * width: the track carries a large responsive padding-inline, so
    // arithmetic from scrollLeft is off by that padding and drifts as the
    // viewport changes.
    function padLeft() {
      return parseFloat(getComputedStyle(track).paddingLeft) || 0;
    }
    function contentLeft() {
      return track.getBoundingClientRect().left + padLeft();
    }
    function index() {
      var base = contentLeft();
      var best = 0;
      var bestDist = Infinity;
      all().forEach(function (c, i) {
        var d = Math.abs(c.getBoundingClientRect().left - base);
        if (d < bestDist) { bestDist = d; best = i; }
      });
      return best;
    }
    // Glides on the theme's curve (yoni-loop.js). While a glide runs, the arrows count from
    // the card it is headed to, so quick clicks add up.
    var headed = 0;
    function goTo(i) {
      var list = all();
      headed = Math.max(0, Math.min(list.length - 1, i));
      var delta = list[headed].getBoundingClientRect().left - contentLeft();
      if (window.yoniGlide) window.yoniGlide(track, track.scrollLeft + delta);
      else track.scrollBy({ left: delta, behavior: reducedMotion() ? 'auto' : 'smooth' });
    }
    function from() { return window.yoniGlide && window.yoniGlide.target(track) !== null ? headed : index(); }

    // ---- dots ---------------------------------------------------------------
    var dots = [];
    if (dotsWrap) {
      dotsWrap.textContent = '';
      cards.forEach(function (_, i) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'yoni-bcards__dot' + (i === 0 ? ' is-active' : '');
        dot.setAttribute('aria-label', 'Go to card ' + (i + 1) + ' of ' + cards.length);
        dot.addEventListener('click', function () { goTo(loop && loop.active ? loop.start + i : i); });
        dotsWrap.appendChild(dot);
        dots.push(dot);
      });
    }

    if (prev) prev.addEventListener('click', function () { goTo(from() - 1); });
    if (next) next.addEventListener('click', function () { goTo(from() + 1); });

    function sync() {
      var i = index();
      dots.forEach(function (d, j) { d.classList.toggle('is-active', j === i % n); });
      // Disable the arrows at the ends rather than letting them no-op silently (an endless row has none).
      var ends = !(loop && loop.active);
      var atStart = ends && (track.scrollLeft <= 1 || i === 0);
      var atEnd = ends && track.scrollLeft >= track.scrollWidth - track.clientWidth - 1;
      if (prev) prev.disabled = atStart;
      if (next) next.disabled = atEnd;
    }

    track.addEventListener('scroll', sync, { passive: true });
    if ('ResizeObserver' in window) {
      var lastW = track.clientWidth;
      new ResizeObserver(function () {
        // Guarded on the track's own width: content changes fire this too, and
        // re-syncing on those is pointless churn.
        if (Math.abs(track.clientWidth - lastW) < 2) return;
        lastW = track.clientWidth;
        if (loop) loop.refresh();
        sync();
      }).observe(track);
    }
    sync();
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-yoni-bcards]').forEach(init);
  }

  ready(function () { initAll(document); });
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
